import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { PROJECTS, findProject } from "@/data/projects";
import { useLang } from "@/i18n/LanguageContext";

export default function ProjectDetail() {
  const { code } = useParams();
  const { t, lang } = useLang();
  const project = findProject(code);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [code]);

  if (!project) return <Navigate to="/" replace />;

  const content = lang === "ko" ? project.ko : project.en;
  const domain = lang === "ko" ? project.domain_ko : project.domain_en;
  const duration = lang === "ko" ? project.duration_ko : project.duration;
  const cohort = lang === "ko" ? project.cohort_ko : project.cohort;
  const metricLabel = lang === "ko" ? project.metric_label_ko : project.metric_label_en;
  const P = t.projects;

  const idx = PROJECTS.findIndex((p) => p.code === project.code);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];
  const nextContent = lang === "ko" ? next.ko : next.en;

  return (
    <main data-testid="project-detail-page" className="relative bg-[#101012] min-h-screen">
      <Nav />

      {/* Hero */}
      <section className="relative pt-32 lg:pt-40 pb-20 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={project.hero_img}
            alt={content.title}
            className="w-full h-full object-cover"
            style={{ filter: "grayscale(0.7) contrast(1.05) brightness(0.35)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#101012]/40 via-[#101012]/70 to-[#101012]" />
          <div className="absolute inset-0 grid-lines opacity-50" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <Link
            to="/#projects"
            data-testid="project-back-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-white/60 hover:text-white transition-colors mb-12"
          >
            <ArrowLeft size={14} /> {P.back}
          </Link>

          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-9">
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <span className="font-mono text-[10px] tracking-widest uppercase text-[#FF3333] border border-[#FF3333]/40 px-3 py-1.5">
                  {project.code}
                </span>
                <span className="font-mono text-[10px] tracking-widest uppercase text-white/60 border border-white/15 px-3 py-1.5">
                  {project.year}
                </span>
                <span className="font-mono text-[10px] tracking-widest uppercase text-white/60 border border-white/15 px-3 py-1.5">
                  {domain}
                </span>
              </div>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="display-1 text-white text-4xl md:text-6xl lg:text-7xl"
              >
                {content.title}
              </motion.h1>
            </div>
          </div>
        </div>
      </section>

      {/* Meta strip */}
      <section className="border-b border-white/10 bg-[#0a0a0c]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          <MetaCell label={P.year_label} value={project.year} />
          <MetaCell label={P.duration_label} value={duration} />
          <MetaCell label={P.cohort_label} value={cohort} />
          <MetaCell label={P.metric_label} value={project.metric} valueClass="text-[#FF3333]" />
        </div>
      </section>

      {/* Body */}
      <section className="border-b border-white/10 py-24 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 lg:gap-12">
          <aside className="col-span-12 lg:col-span-3">
            <div className="sticky top-28 space-y-8">
              <div>
                <div className="label-mono mb-3">{P.methods_used}</div>
                <ul className="space-y-2">
                  {content.methods_used.map((m) => (
                    <li key={m} className="font-mono text-[10px] tracking-widest uppercase text-white/70 border border-white/15 px-3 py-2">
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="label-mono mb-2">{metricLabel}</div>
                <div className="font-display font-bold text-5xl text-white">
                  {project.metric}<span className="text-[#FF3333]">.</span>
                </div>
              </div>
            </div>
          </aside>

          <div className="col-span-12 lg:col-span-9 space-y-16">
            <Block label={P.overview} body={content.overview} />
            <Block label={P.challenge} body={content.challenge} />
            <Block label={P.approach} body={content.approach} />

            <div>
              <div className="label-mono mb-6">{P.findings}</div>
              <ul className="space-y-4">
                {content.findings.map((f, i) => (
                  <li key={i} className="flex gap-5 border-t border-white/10 pt-5">
                    <span className="font-mono text-[10px] tracking-widest uppercase text-[#FF3333] w-10 shrink-0 pt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-white/85 text-base leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Next project */}
      <section className="border-b border-white/10">
        <Link
          to={`/projects/${next.code}`}
          data-testid="project-next-link"
          className="group block py-16 lg:py-20 hover:bg-[#161618] transition-colors"
        >
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 lg:col-span-2 label-mono">{P.next_project}</div>
            <div className="col-span-12 lg:col-span-8">
              <h3 className="font-display font-bold text-3xl md:text-5xl text-white tracking-tight group-hover:text-[#FF3333] transition-colors">
                {nextContent.title}
              </h3>
            </div>
            <div className="col-span-12 lg:col-span-2 flex lg:justify-end">
              <ArrowRight size={36} className="text-white/40 group-hover:text-[#FF3333] group-hover:translate-x-2 transition-all" />
            </div>
          </div>
        </Link>
      </section>

      <Footer />
    </main>
  );
}

function Block({ label, body }) {
  return (
    <div>
      <div className="label-mono mb-4">{label}</div>
      <p className="text-white/80 text-base md:text-lg leading-relaxed">{body}</p>
    </div>
  );
}

function MetaCell({ label, value, valueClass = "text-white" }) {
  return (
    <div className="bg-[#0a0a0c] px-6 py-4">
      <div className="label-mono mb-2">{label}</div>
      <div className={`font-display font-bold text-2xl ${valueClass}`}>{value}</div>
    </div>
  );
}
